import { getRoutes } from "src/utilities/routes";
import { API_URL } from "src/utilities";

export interface SmsHistoryItem {
  id: number;
  direction: number; // 0 inbound, 1 outbound
  phoneNumber: string;
  customerRef?: string | null;
  customerName?: string | null;
  senderRef?: string | null;
  senderUserName?: string | null;
  message: string;
  status?: string | null;
  providerStatus?: number | null;
  providerStatusText?: string | null;
  providerMessageId?: string | null;
  occurredOn: string;
  error?: string | null;
  isRead?: boolean;
  readByUserId?: string | null;
  readByUserName?: string | null;
}

export interface SmsHistoryResponse {
  items: SmsHistoryItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export async function sendSmsDirect(
  token: string,
  payload: { phoneNumber: string; customerRef: string; message: string }
): Promise<void> {
  const routes = getRoutes();
  const response = await fetch(`${routes.SmsHistoryRoutes}/send`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      Accept: "application/json",
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      errorData?.data ||
      `Failed to send sms: ${response.statusText}`;
    throw new Error(errorMessage);
  }
}

export async function fetchSmsHistory(
  token: string,
  params: {
    phone?: string;
    customerRef?: string;
    page?: number;
    pageSize?: number;
  }
): Promise<SmsHistoryResponse> {
  const routes = getRoutes();
  const search = new URLSearchParams();
  if (params.phone) search.append("phone", params.phone);
  if (params.customerRef) search.append("customerRef", params.customerRef);
  if (params.page !== undefined) search.append("page", String(params.page));
  if (params.pageSize !== undefined)
    search.append("pageSize", String(params.pageSize));

  const response = await fetch(
    `${routes.SmsHistoryRoutes}?${search.toString()}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to fetch sms history: ${response.statusText}`;
    throw new Error(errorMessage);
  }

  const json = await response.json();
  return json.data as SmsHistoryResponse;
}

export async function getUnreadInboundSmsCount(token: string): Promise<number> {
  const routes = getRoutes();

  try {
    // دریافت تمام پیام‌های دریافتی (direction = 0) با صفحه‌بندی بزرگ
    const response = await fetch(
      `${routes.SmsHistoryRoutes}?page=0&pageSize=1000`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      return 0;
    }

    const json = await response.json();
    const data = json.data as SmsHistoryResponse;

    // شمارش پیام‌های دریافتی (direction = 0) که خوانده نشده‌اند (isRead = false)
    const unreadCount = data.items.filter(
      (item) => item.direction === 0 && !item.isRead
    ).length;

    return unreadCount;
  } catch (error) {
    console.error("Error fetching unread SMS count:", error);
    return 0;
  }
}

export async function getUnreadInboundSmsCountByPhone(
  token: string,
  phone: string
): Promise<number> {
  const routes = getRoutes();

  try {
    // دریافت پیام‌های دریافتی بر اساس شماره موبایل
    const response = await fetch(
      `${routes.SmsHistoryRoutes}?phone=${encodeURIComponent(phone)}&page=0&pageSize=1000`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      return 0;
    }

    const json = await response.json();
    const data = json.data as SmsHistoryResponse;

    // شمارش پیام‌های دریافتی (direction = 0) که خوانده نشده‌اند (isRead = false)
    const unreadCount = data.items.filter(
      (item) => item.direction === 0 && !item.isRead
    ).length;

    return unreadCount;
  } catch (error) {
    console.error("Error fetching unread SMS count by phone:", error);
    return 0;
  }
}

export async function getUnreadInboundSms(
  token: string
): Promise<SmsHistoryItem[]> {
  const routes = getRoutes();

  try {
    // دریافت تمام پیام‌های دریافتی (direction = 0) با صفحه‌بندی بزرگ
    const response = await fetch(
      `${routes.SmsHistoryRoutes}?page=0&pageSize=1000`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      }
    );

    if (!response.ok) {
      return [];
    }

    const json = await response.json();
    const data = json.data as SmsHistoryResponse;

    // فیلتر کردن پیام‌های دریافتی (direction = 0) که خوانده نشده‌اند (isRead = false)
    const unreadSms = data.items.filter(
      (item) => item.direction === 0 && !item.isRead
    );

    // مرتب‌سازی بر اساس تاریخ (جدیدترین اول)
    return unreadSms.sort(
      (a, b) =>
        new Date(b.occurredOn).getTime() - new Date(a.occurredOn).getTime()
    );
  } catch (error) {
    console.error("Error fetching unread SMS:", error);
    return [];
  }
}

export async function markSmsAsRead(
  token: string,
  smsId: number
): Promise<void> {
  const routes = getRoutes();
  const response = await fetch(
    `${routes.SmsHistoryRoutes}/${smsId}/mark-as-read`,
    {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "application/json",
      },
    }
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    const errorMessage =
      errorData?.message ||
      errorData?.title ||
      `Failed to mark SMS as read: ${response.statusText}`;
    throw new Error(errorMessage);
  }
}
